/**
 * Simple, centralised i18n for the mobile app (/app/*).
 *
 * v1: English only. Structure is ready for Kissi / Krio / French.
 * Persists language choice to localStorage.
 */

import { useEffect, useState, useCallback } from 'react';

export const SUPPORTED_LANGS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  // Ready to enable when translations are provided:
  // { code: 'kissi', label: 'Kissi', flag: '🇱🇷' },
  // { code: 'krio', label: 'Krio', flag: '🇸🇱' },
  // { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const DICT = {
  en: {
    'home.title': 'Kioo Radio',
    'home.subtitle': '98.1 FM · The Gift of Good News',
    'home.listenLive': 'Listen Live',
    'home.pause': 'Pause',
    'home.buffering': 'Buffering…',
    'home.nowOnAir': 'Now On Air',
    'home.upNext': 'Up Next',
    'home.sendVoice': 'Send a Voice Message',
    'home.sendVoiceSub': 'Say hello, share a testimony or request a song',
    'home.error': 'Stream unavailable. Tap play to try again.',
    'home.offline': 'You appear to be offline.',
    'nav.home': 'Home',
    'nav.programs': 'Programs',
    'nav.message': 'Message',
    'nav.contact': 'Contact',
    'schedule.title': 'Program Schedule',
    'schedule.subtitle': 'Pick a day to see programs (times in GMT)',
    'schedule.today': 'Today',
    'schedule.mon': 'Mon',
    'schedule.tue': 'Tue',
    'schedule.wed': 'Wed',
    'schedule.thu': 'Thu',
    'schedule.fri': 'Fri',
    'schedule.sat': 'Sat',
    'schedule.sun': 'Sun',
    'schedule.onAirNow': 'ON AIR NOW',
    'message.title': 'Send a Message to Kioo Radio',
    'message.comingSoon': 'Recording will be available shortly.',
    'message.comingSoonBody':
      'We are finishing the voice-message feature. In the meantime, please contact Kioo using the numbers below.',
    'contact.title': 'Contact Kioo Radio',
    'contact.callKioo': 'Call Kioo',
    'contact.whatsapp': 'WhatsApp Kioo',
    'contact.email': 'Email',
    'contact.facebook': 'Facebook',
    'contact.website': 'Website',
    'contact.studio': 'Studio',
    'install.prompt': 'Install Kioo Radio on your phone',
    'install.action': 'Install',
    'install.dismiss': 'Not now',
  },
};

const LS_KEY = 'kioo_mobile_lang';

let currentLang =
  (typeof window !== 'undefined' && localStorage.getItem(LS_KEY)) || 'en';
const listeners = new Set();

const notify = () => listeners.forEach((cb) => cb(currentLang));

export const getLanguage = () => currentLang;

export const setLanguage = (code) => {
  if (!DICT[code]) return;
  currentLang = code;
  try {
    localStorage.setItem(LS_KEY, code);
  } catch (_e) {
    /* ignore */
  }
  notify();
};

export const translate = (key, fallback) => {
  const table = DICT[currentLang] || DICT.en;
  return table[key] || fallback || key;
};

export function useT() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((n) => n + 1);
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);
  return useCallback((k, f) => translate(k, f), []);
}
